import type { ConsoleOperations, ConsoleRun } from '@rsecure/brand-contracts/console';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import {
  Badge,
  Empty,
  ErrorState,
  Json,
  Loading,
  Ltr,
  Pager,
  StatusBadge,
  Time,
} from '../components/ui';
import { api } from '../lib/api';
import { fmtDuration, fmtNum } from '../lib/format';
import { useUrlState } from '../lib/url';

/** Stage (run kind) → the pipeline step it implements, in pipeline order. */
const STAGE_ORDER = [
  'discover',
  'fetch',
  'webhook',
  'verify',
  'assets',
  'normalize',
  'publish-assets',
  'publish-revision',
  'refresh',
  'retry',
  'reconcile',
];

function RunDetail({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['run', id], queryFn: () => api.run(id) });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading />;
  const r = q.data;
  return (
    <section className="card drawer" aria-labelledby="h-run" data-testid="run-detail">
      <div className="card-h">
        <h2 id="h-run">{r.kind} run</h2>
        <StatusBadge kind="run" value={r.status} />
      </div>
      <div className="card-b grid" style={{ gap: 12 }}>
        <dl className="dl" style={{ gridTemplateColumns: '110px minmax(0,1fr)' }}>
          <dt>Run ID</dt>
          <dd>
            <Ltr mono>{r.id}</Ltr>
          </dd>
          <dt>Trigger</dt>
          <dd>{r.trigger}</dd>
          <dt>Brand</dt>
          <dd>
            {r.entity ? (
              <>
                <Link className="link" to={`/brands/${r.entity.slug}/timeline`}>
                  {r.entity.name}
                </Link>
                {r.entityVia === 'domain' && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    {' '}
                    · resolved via primary domain <Ltr>{r.requestKey}</Ltr>
                  </span>
                )}
              </>
            ) : r.requestKey ? (
              <Ltr>{r.requestKey}</Ltr>
            ) : (
              <span className="faint">catalog-wide</span>
            )}
          </dd>
          <dt>Job</dt>
          <dd>
            {r.jobId ? (
              <Ltr mono>{r.jobId}</Ltr>
            ) : (
              <span className="faint">inline (no queue job)</span>
            )}
          </dd>
          <dt>Started</dt>
          <dd>
            <Time iso={r.startedAt} />
          </dd>
          <dt>Finished</dt>
          <dd>
            <Time iso={r.finishedAt} /> <span className="muted">· {fmtDuration(r.durationMs)}</span>
          </dd>
        </dl>
        {r.error && (
          <div className="notice bad">
            <div className="mono" style={{ overflowWrap: 'anywhere' }}>
              {r.error}
            </div>
          </div>
        )}
        {r.stats != null && (
          <pre className="json ltr" dir="ltr">
            {JSON.stringify(r.stats, null, 2)}
          </pre>
        )}
      </div>
    </section>
  );
}

function Health({ o }: { o: ConsoleOperations }) {
  const s = o.status;
  const today = o.apiUsage[0];
  const budget = s.api.budgetToday;
  return (
    <div className="grid three">
      <section className="card" aria-labelledby="h-worker">
        <div className="card-h">
          <h2 id="h-worker">Worker & scheduler</h2>
          {s.worker?.leaseHeld ? (
            <Badge tone="ok">worker lease held</Badge>
          ) : (
            <Badge tone="warn">no active worker</Badge>
          )}
        </div>
        <div className="card-b">
          <dl className="dl" style={{ gridTemplateColumns: '140px minmax(0,1fr)' }}>
            <dt>Lease holder</dt>
            <dd>
              {s.worker?.holder ?? <span className="faint">none — jobs are not executing</span>}
            </dd>
            <dt>This API process</dt>
            <dd>{s.worker?.thisProcess ?? '—'}</dd>
            <dt>Scheduler</dt>
            <dd>
              {o.scheduler.enabled ? <Badge tone="ok">enabled</Badge> : <Badge>disabled</Badge>}
              <div className="muted mono" style={{ fontSize: 12 }}>
                refresh {o.scheduler.refreshCron} · retry {o.scheduler.retryCron} · reconcile{' '}
                {o.scheduler.reconcileCron}
              </div>
            </dd>
            <dt>Dead letters</dt>
            <dd>
              {s.deadLetters === undefined ? (
                <span className="faint">queue not connected</span>
              ) : (
                fmtNum(s.deadLetters)
              )}
            </dd>
            <dt>Refresh backlog</dt>
            <dd>{fmtNum(s.refreshBacklog)} due</dd>
            <dt>Inconsistencies</dt>
            <dd>{s.inconsistencies ?? <span className="faint">no reconcile run recorded</span>}</dd>
          </dl>
        </div>
      </section>
      <section className="card" aria-labelledby="h-budget">
        <div className="card-h">
          <h2 id="h-budget">Brandfetch budget</h2>
        </div>
        <div className="card-b">
          <dl className="dl" style={{ gridTemplateColumns: '140px minmax(0,1fr)' }}>
            <dt>Requests today</dt>
            <dd>
              {fmtNum(s.api.requestsToday)}{' '}
              {budget != null ? `of ${fmtNum(budget)} budget` : '(no daily budget set)'}
            </dd>
            <dt>Remaining today</dt>
            <dd>
              {budget != null ? (
                fmtNum(Math.max(0, budget - s.api.requestsToday))
              ) : (
                <span className="faint">unlimited (not configured)</span>
              )}
            </dd>
            <dt>Failures today</dt>
            <dd>{fmtNum(s.api.failuresToday)}</dd>
            <dt>Provider quota</dt>
            <dd>
              {s.api.providerQuota != null ? (
                <>
                  {fmtNum(s.api.providerUsage ?? 0)} / {fmtNum(s.api.providerQuota)} used
                  <div className="muted" style={{ fontSize: 12 }}>
                    reported <Time iso={s.api.providerReportedAt} relative />
                  </div>
                </>
              ) : today?.providerQuota != null ? (
                <>
                  {fmtNum(today.providerUsage ?? 0)} / {fmtNum(today.providerQuota)} used on{' '}
                  {today.day}
                </>
              ) : (
                <span className="faint">not reported (no live request yet)</span>
              )}
            </dd>
          </dl>
          {o.apiUsage.length > 0 && (
            <table className="t" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col">Requests</th>
                  <th scope="col">Failures</th>
                </tr>
              </thead>
              <tbody>
                {o.apiUsage.slice(0, 7).map((u) => (
                  <tr key={u.day + u.endpoint}>
                    <td className="mono">{u.day}</td>
                    <td className="num">{u.requests}</td>
                    <td className="num">{u.failures}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      <section className="card" aria-labelledby="h-sources">
        <div className="card-h">
          <h2 id="h-sources">Sources &amp; fetch state</h2>
        </div>
        <div className="card-b">
          <dl className="dl" style={{ gridTemplateColumns: '140px minmax(0,1fr)' }}>
            <dt>Snapshots</dt>
            <dd>
              {o.snapshotsBySource.map((x) => (
                <div key={x.value}>
                  {x.value === 'domain'
                    ? 'live Brandfetch'
                    : x.value === 'webhook'
                      ? 'webhook'
                      : x.value === 'legacy_bulk' || x.value === 'legacy_v1'
                        ? `historical import (${x.value})`
                        : x.value}
                  : <strong className="num">{fmtNum(x.count)}</strong>
                </div>
              ))}
            </dd>
            <dt>Fetch state</dt>
            <dd>
              {o.fetchState.byStatus.length ? (
                o.fetchState.byStatus.map((x) => `${x.value} ${fmtNum(x.count)}`).join(' · ')
              ) : (
                <span className="faint">no domain fetched yet</span>
              )}
              <div className="muted" style={{ fontSize: 12 }}>
                {fmtNum(o.fetchState.deferred)} deferred · {fmtNum(o.fetchState.due)} due
              </div>
            </dd>
            <dt>Webhooks</dt>
            <dd>
              {o.webhooks.byStatus.length ? (
                o.webhooks.byStatus.map((x) => `${x.value} ${x.count}`).join(' · ')
              ) : (
                <span className="faint">none received</span>
              )}
            </dd>
          </dl>
        </div>
      </section>
    </div>
  );
}

export function OperationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sp, set } = useUrlState();
  const ops = useQuery({
    queryKey: ['operations'],
    queryFn: api.operations,
    refetchInterval: 30_000,
  });
  const limit = Number(sp.get('limit') ?? 50);
  const offset = Number(sp.get('offset') ?? 0);
  const params = {
    kind: sp.get('kind'),
    status: sp.get('status'),
    trigger: sp.get('trigger'),
    limit,
    offset,
  };
  const runs = useQuery({
    queryKey: ['runs', params],
    queryFn: () => api.runs(params),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const suffix = sp.toString() ? `?${sp}` : '';
  const byKind = [...(ops.data?.runsByKind ?? [])].sort(
    (a, b) => STAGE_ORDER.indexOf(a.kind) - STAGE_ORDER.indexOf(b.kind),
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ETL operations</h1>
          <p>
            Ingestion runs, queues, provider budget and worker health — derived from persisted V3
            records and pg-boss.
          </p>
        </div>
        {ops.data && (
          <span className="muted" style={{ fontSize: 12 }}>
            snapshot <Time iso={ops.data.generatedAt} relative />
          </span>
        )}
      </div>
      {ops.isError ? (
        <ErrorState error={ops.error} onRetry={() => ops.refetch()} />
      ) : !ops.data ? (
        <Loading rows={6} />
      ) : (
        <div className="grid" style={{ gap: 16, marginBottom: 16 }}>
          <section className="stats" style={{ marginBottom: 0 }} aria-label="Job state">
            <div className="stat">
              <div className="label">Queued jobs</div>
              <div className="value">{fmtNum(ops.data.status.jobs.queued)}</div>
              <div className="sub">pg-boss, all stage queues</div>
            </div>
            <div className="stat">
              <div className="label">Active jobs</div>
              <div className="value">{fmtNum(ops.data.status.jobs.active)}</div>
              <div className="sub">
                {fmtNum(byKind.reduce((s, k) => s + k.running, 0))} runs marked running
              </div>
            </div>
            <div className={`stat ${ops.data.status.jobs.failed ? 'tone-bad' : ''}`}>
              <div className="label">Failed jobs</div>
              <div className="value">{fmtNum(ops.data.status.jobs.failed)}</div>
              <div className="sub">retained by pg-boss</div>
            </div>
            <div className="stat">
              <div className="label">Deferred</div>
              <div className="value">
                {fmtNum(ops.data.queues?.reduce((s, q) => s + q.deferred, 0) ?? 0)}
              </div>
              <div className="sub">
                {fmtNum(ops.data.fetchState.deferred)} domains in fetch backoff
              </div>
            </div>
            <div className={`stat ${ops.data.status.deadLetters ? 'tone-bad' : ''}`}>
              <div className="label">Dead letters</div>
              <div className="value">
                {ops.data.status.deadLetters === undefined
                  ? '—'
                  : fmtNum(ops.data.status.deadLetters)}
              </div>
              <div className="sub">bi.dead</div>
            </div>
            <div className={`stat ${ops.data.status.publicationFailures24h ? 'tone-bad' : ''}`}>
              <div className="label">Publication failures</div>
              <div className="value">{fmtNum(ops.data.status.publicationFailures24h)}</div>
              <div className="sub">last 24h</div>
            </div>
          </section>
          <Health o={ops.data} />
          <div className="grid two">
            <section className="card" aria-labelledby="h-stages">
              <div className="card-h">
                <h2 id="h-stages">Runs by stage</h2>
                <span className="muted" style={{ fontSize: 12 }}>
                  brand_ingestion_runs
                </span>
              </div>
              <div className="card-b flush table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th scope="col">Stage</th>
                      <th scope="col">Succeeded</th>
                      <th scope="col">Failed</th>
                      <th scope="col">Skipped</th>
                      <th scope="col">Running</th>
                      <th scope="col">Last success</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byKind.map((k) => (
                      <tr
                        key={k.kind}
                        className="row-link"
                        onClick={() => set({ kind: k.kind, status: null })}
                      >
                        <td className="mono">{k.kind}</td>
                        <td className="num">{fmtNum(k.succeeded)}</td>
                        <td className="num" style={{ color: k.failed ? 'var(--bad)' : undefined }}>
                          {fmtNum(k.failed)}
                          {k.failed24h ? <span className="muted"> ({k.failed24h} 24h)</span> : null}
                        </td>
                        <td className="num">{fmtNum(k.skipped)}</td>
                        <td className="num">{fmtNum(k.running)}</td>
                        <td>
                          <Time iso={k.lastSucceededAt} relative />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="card" aria-labelledby="h-queues">
              <div className="card-h">
                <h2 id="h-queues">Queues (pg-boss)</h2>
              </div>
              <div className="card-b flush table-wrap">
                {ops.data.queues ? (
                  <table className="t">
                    <thead>
                      <tr>
                        <th scope="col">Queue</th>
                        <th scope="col">Policy</th>
                        <th scope="col">Queued</th>
                        <th scope="col">Active</th>
                        <th scope="col">Deferred</th>
                        <th scope="col">Failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ops.data.queues.map((q) => (
                        <tr key={q.name}>
                          <td className="mono">{q.name}</td>
                          <td className="muted">{q.policy ?? '—'}</td>
                          <td className="num">{q.queued}</td>
                          <td className="num">{q.active}</td>
                          <td className="num">{q.deferred}</td>
                          <td
                            className="num"
                            style={{ color: q.failed ? 'var(--bad)' : undefined }}
                          >
                            {q.failed}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="muted" style={{ padding: 16, margin: 0 }}>
                    The V3 API process has no queue connection; queue depth is unavailable.
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>
      )}

      <div className="page-head" style={{ marginTop: 8 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Ingestion runs</h2>
      </div>
      <div className="filters">
        <select
          className="select"
          aria-label="Stage"
          value={sp.get('kind') ?? ''}
          onChange={(e) => set({ kind: e.target.value || null })}
        >
          <option value="">Stage: all</option>
          {STAGE_ORDER.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select
          className="select"
          aria-label="Status"
          value={sp.get('status') ?? ''}
          onChange={(e) => set({ status: e.target.value || null })}
        >
          <option value="">Status: all</option>
          {['running', 'succeeded', 'failed', 'skipped'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="select"
          aria-label="Trigger"
          value={sp.get('trigger') ?? ''}
          onChange={(e) => set({ trigger: e.target.value || null })}
        >
          <option value="">Trigger: all</option>
          {(ops.data?.runsByTrigger ?? []).map((x) => (
            <option key={x.value} value={x.value ?? ''}>
              {x.value} ({fmtNum(x.count)})
            </option>
          ))}
        </select>
      </div>
      <div className={id ? 'split' : undefined}>
        <section className="card">
          {runs.isError ? (
            <ErrorState error={runs.error} onRetry={() => runs.refetch()} />
          ) : !runs.data ? (
            <Loading rows={10} />
          ) : runs.data.total === 0 ? (
            <Empty
              title="No ingestion runs match"
              sub="Runs appear here as the worker, CLI or backfill executes pipeline stages."
            />
          ) : (
            <>
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th scope="col">Stage</th>
                      <th scope="col">Status</th>
                      <th scope="col">Brand</th>
                      <th scope="col" className="hide-sm">
                        Trigger
                      </th>
                      <th scope="col">Started</th>
                      <th scope="col" className="hide-sm">
                        Duration
                      </th>
                      <th scope="col" className="hide-sm">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.data.items.map((r: ConsoleRun) => (
                      <tr
                        key={r.id}
                        className={`row-link${r.id === id ? ' selected' : ''}`}
                        onClick={() => navigate(`/operations/runs/${r.id}${suffix}`)}
                        data-testid="run-row"
                      >
                        <td className="mono">{r.kind}</td>
                        <td>
                          <StatusBadge kind="run" value={r.status} />
                        </td>
                        <td>
                          {r.entity ? (
                            <Link
                              className="link"
                              to={`/brands/${r.entity.slug}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r.entity.name}
                            </Link>
                          ) : r.requestKey ? (
                            <Ltr>{r.requestKey}</Ltr>
                          ) : (
                            <span className="faint">—</span>
                          )}
                        </td>
                        <td className="hide-sm muted">{r.trigger}</td>
                        <td>
                          <Time iso={r.startedAt} relative />
                        </td>
                        <td className="hide-sm num">{fmtDuration(r.durationMs)}</td>
                        <td
                          className="hide-sm err"
                          style={{
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.error ?? ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager
                total={runs.data.total}
                limit={limit}
                offset={offset}
                onChange={(o) => set({ offset: String(o) }, false)}
                onLimit={(l) => set({ limit: String(l) })}
              />
            </>
          )}
        </section>
        {id && <RunDetail id={id} />}
      </div>
      {ops.data && ops.data.recentFailures.length > 0 && (
        <section className="card" style={{ marginTop: 16 }}>
          <div className="card-h">
            <h2>Recent failures</h2>
          </div>
          <div className="card-b">
            <Json
              value={ops.data.recentFailures.map((f) => ({
                id: f.id,
                kind: f.kind,
                entity: f.entity?.slug,
                error: f.error,
              }))}
              summary={`${ops.data.recentFailures.length} most recent failed runs`}
            />
          </div>
        </section>
      )}
    </>
  );
}
